import { useEffect, useRef, useState } from 'react';

import { useTheme } from '@efira/ui';

import { TURNSTILE_SITE_KEY } from '../config';

/**
 * Виджет Cloudflare Turnstile на форме регистрации.
 *
 * ЗАЧЕМ ОН ПОЯВИЛСЯ ЗДЕСЬ И ПОЧЕМУ ЭТО БЫЛ БЛОКЕР. Сервер проверяет Turnstile
 * на регистрации, и в production ключ обязателен: без него `assertProductionEnv`
 * не даёт серверу стартовать (docs/DECISIONS.md, F6 п. 8). Проверка отклоняет
 * запрос без токена с кодом `turnstile_failed`. Форма регистрации токена не
 * присылала - виджета не существовало. То есть на боевом сервере **любая**
 * регистрация возвращала бы отказ, а локально всё работало: без секретного
 * ключа сервер проверку пропускает.
 *
 * Найдено при подготовке фронтендов к развёртыванию, до первой регистрации
 * живого пользователя.
 *
 * БЕЗ КЛЮЧА ВИДЖЕТ НЕ РИСУЕТСЯ. Это состояние разработки: сервер без секрета
 * проверку пропускает, и требовать ключи Cloudflare ради `pnpm dev` значило бы
 * поставить провижининг условием локального запуска.
 *
 * Публичный ключ секретом НЕ является: виджет рисуется в браузере
 * пользователя, и ключ виден в исходном коде страницы по определению.
 * Секретный ключ живёт только в окружении сервера - Vite подставляет в сборку
 * лишь переменные с приставкой `VITE_`, и у секрета её нет.
 */

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
      theme: 'dark' | 'light' | 'auto';
      language: string;
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Скрипт грузится один раз на вкладку, даже если форм несколько. */
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise !== null) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('скрипт Turnstile не загрузился'));
    document.head.append(script);
  });

  return scriptPromise;
}

export interface TurnstileProps {
  /** Токен решения. null - пользователь ещё не прошёл проверку. */
  onToken: (token: string | null) => void;
  /**
   * Проверка недоступна (Cloudflare не ответил). Регистрация при этом
   * продолжает работать - сервер пропускает проверку, - и форма не должна
   * ждать токена, которого не будет.
   */
  onFailed?: () => void;
}

export function Turnstile({ onToken, onFailed }: TurnstileProps) {
  const holder = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  // Виджет живёт в закрытом shadow DOM и снаружи не виден: состояние проверки
  // выносится атрибутом - для сквозных сценариев и для отладки глазами.
  const [state, setState] = useState<'pending' | 'passed' | 'failed'>('pending');
  const { theme } = useTheme();

  useEffect(() => {
    const siteKey = TURNSTILE_SITE_KEY;

    if (siteKey === undefined || holder.current === null) {
      return;
    }

    const element = holder.current;
    let widgetId: string | null = null;
    let disposed = false;

    void loadScript()
      .then(() => {
        if (disposed || window.turnstile === undefined) {
          return;
        }

        widgetId = window.turnstile.render(element, {
          sitekey: siteKey,
          callback: (token) => {
            setState('passed');
            onToken(token);
          },
          // Токен живёт минуты: просроченный сервер отклонит, и лучше
          // потребовать проверку заново, чем отправить заведомо мёртвый.
          'expired-callback': () => {
            setState('pending');
            onToken(null);
          },
          'error-callback': () => {
            setFailed(true);
            onFailed?.();
            setState('failed');
            onToken(null);
          },
          theme,
          language: 'ru',
        });
      })
      .catch(() => {
        // Недоступность Cloudflare не должна закрывать регистрацию совсем:
        // сервер в этом случае пропускает проверку, о чём пишет в журнал.
        setFailed(true);
        onFailed?.();
        onToken(null);
      });

    return () => {
      disposed = true;

      if (widgetId !== null && window.turnstile !== undefined) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [onToken, onFailed, theme]);

  if (TURNSTILE_SITE_KEY === undefined) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2" data-turnstile={state}>
      {/* Место под виджет занято заранее: иначе форма прыгает, когда он догрузится. */}
      <div ref={holder} className="min-h-[65px]" />
      {/*
        СТРОКИ «ПРОВЕРЯЕМ, ЧТО ВЫ НЕ РОБОТ…» ЗДЕСЬ БОЛЬШЕ НЕТ (N-19): виджет
        Cloudflare в это же время пишет «Идёт проверка…» сам, и два
        сообщения об одном стояли в пяти сантиметрах друг от друга. Пока
        проверка идёт, форма говорит об этом причиной у кнопки отправки.
      */}

      {failed ? (
        <p className="text-foreground/75 text-sm">
          Проверка Cloudflare недоступна. Регистрация продолжит работать — если письмо не придёт,
          попробуйте ещё раз через минуту.
        </p>
      ) : null}
    </div>
  );
}
